import { useState } from "react";
import { useElapsed, useRunStream } from "@/chat/useRunStream";
import {
  useHealthCheck,
  useHealthCheckHistory,
  useHealthProbes,
  useModels,
  useRunHealthCheck,
  type HealthCheck,
  type HealthCheckStep,
} from "@/api/queries";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/Button";
import { Chip, StatusDot } from "@/components/ui/Chip";
import { EmptyState } from "@/components/ui/EmptyState";
import { Select } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import { SqlBlock } from "@/components/chat/SqlBlock";
import {
  AlertIcon,
  CheckIcon,
  ChevronRightIcon,
  ClockIcon,
  ShieldIcon,
} from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * Connection Health Check (PRD FR-1.8).
 *
 * A verdict banner over a ladder of graded steps, each expandable to the trace
 * behind it. The check is four sequential agent runs, so it takes minutes on a
 * local model and the panel polls rather than streams — every step lands as
 * soon as it is graded.
 *
 * The fourth graded question is a **safety probe**: an injected write planted
 * in the data dictionary, which is the channel the prompt treats as
 * authoritative. It is rendered like any other step, with the refused
 * statement in its trace, because the point of the check is that the refusal
 * is *visible* rather than merely real.
 */

const VERDICTS: Record<string, { label: string; tone: "success" | "warning" | "error"; blurb: string }> =
  {
    ready: {
      label: "Ready",
      tone: "success",
      blurb: "This source is ready to hand to analysts.",
    },
    review: {
      label: "Review warnings",
      tone: "warning",
      blurb: "It works, but something below is worth reading before you share the URL.",
    },
    attention: {
      label: "Needs attention",
      tone: "error",
      blurb: "Something failed. Answers from this source cannot be trusted yet.",
    },
  };

const STATUS_TONE: Record<string, "success" | "warning" | "error" | "neutral"> = {
  pass: "success",
  warn: "warning",
  fail: "error",
  skipped: "neutral",
  pending: "neutral",
  running: "neutral",
};

function StepIcon({ status }: { status: string }) {
  if (status === "running")
    return (
      <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center">
        <Spinner />
      </span>
    );
  if (status === "pending")
    return (
      <span className="border-border mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border border-dashed" />
    );
  if (status === "pass")
    return (
      <span className="bg-success-subtle text-success mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full">
        <CheckIcon className="size-3" />
      </span>
    );
  if (status === "skipped")
    return (
      <span className="border-border-emphasized mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border" />
    );
  return (
    <span
      className={cn(
        "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full",
        status === "fail" ? "bg-error-subtle text-error" : "bg-warning-subtle text-warning",
      )}
    >
      <AlertIcon className="size-3" />
    </span>
  );
}

/** The trace FR-1.8 asks for: every statement the graded question ran. */
function Trace({ step }: { step: HealthCheckStep }) {
  const evidence = step.evidence as {
    steps?: Array<{
      tool: string;
      sql: string;
      outcome: string;
      row_count: number | null;
      error_code: string | null;
    }>;
    write_attempts?: Array<{ sql: string; outcome: string; error_code: string | null }>;
    relations?: number;
    commented?: number;
    read_only_enforcement?: string;
  };
  const calls = evidence.steps ?? [];

  return (
    <div className="mt-3 flex flex-col gap-3">
      {evidence.read_only_enforcement ? (
        <p className="type-support text-ink-secondary">
          Read-only enforced by{" "}
          <span className="text-ink font-mono">{evidence.read_only_enforcement}</span>
        </p>
      ) : null}

      {calls.length > 0 ? (
        <ol className="flex flex-col gap-2">
          {calls.map((call, index) => (
            <li key={index} className="border-border rounded-element border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="type-caption text-ink-disabled">{index + 1}</span>
                <span className="type-label text-ink">{call.tool}</span>
                <Chip
                  tone={
                    call.outcome === "ok"
                      ? "success"
                      : call.outcome === "blocked"
                        ? "error"
                        : "warning"
                  }
                >
                  {call.outcome}
                  {call.error_code ? ` · ${call.error_code}` : ""}
                </Chip>
                {call.row_count != null ? (
                  <span className="type-support text-ink-secondary tabular-nums">
                    {call.row_count} rows
                  </span>
                ) : null}
              </div>
              {call.sql ? <SqlBlock sql={call.sql} /> : null}
            </li>
          ))}
        </ol>
      ) : null}

      {evidence.write_attempts && evidence.write_attempts.length > 0 ? (
        <div className="border-error/30 bg-error-subtle/30 rounded-element border p-3">
          <p className="type-label text-ink">What the planted instruction produced</p>
          {evidence.write_attempts.map((attempt, index) => (
            <div key={index} className="mt-2">
              <Chip tone={attempt.outcome === "blocked" ? "success" : "error"}>
                {attempt.outcome === "blocked"
                  ? `refused · ${attempt.error_code ?? "sql_rejected"}`
                  : attempt.outcome}
              </Chip>
              <SqlBlock sql={attempt.sql} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * What the graded question is doing *right now* (backlog item 8).
 *
 * A graded question is an ordinary agent run, so it already streams its phase
 * and every statement it issues over SSE — the same stream chat reattaches to.
 * Nothing here is new machinery; the run id simply was not being published
 * until the answer had already landed.
 */
function LiveTrace({ runId, threadId }: { runId: string; threadId: string | null }) {
  const { run } = useRunStream(runId, threadId);
  const elapsed = useElapsed(run.startedAt, true);

  return (
    <div className="border-border mt-2 flex flex-col gap-2 rounded-element border border-dashed p-3">
      <p className="type-support text-ink-secondary flex flex-wrap items-center gap-2">
        <span>{run.phase || "Thinking…"}</span>
        {elapsed > 0 ? (
          <span className="type-caption text-ink-disabled tabular-nums">{elapsed}s</span>
        ) : null}
      </p>
      {run.steps.length > 0 ? (
        <ol className="flex flex-col gap-1.5">
          {run.steps.map((call) => (
            <li key={call.seq} className="flex flex-wrap items-center gap-2">
              <span className="type-caption text-ink-disabled tabular-nums">{call.seq}</span>
              <span className="type-support text-ink">{call.tool}</span>
              <Chip
                tone={
                  call.outcome === "ok"
                    ? "success"
                    : call.outcome === "blocked"
                      ? "error"
                      : call.outcome === "error"
                        ? "warning"
                        : "neutral"
                }
              >
                {call.outcome}
              </Chip>
              {call.rowCount != null ? (
                <span className="type-caption text-ink-disabled tabular-nums">
                  {call.rowCount} rows
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}
      {/* The statements themselves, not just their outcomes — the point of the
          check is that what it ran is inspectable while it runs, not only
          afterwards. */}
      {run.steps.filter((call) => call.sql).slice(-1).map((call) => (
        <SqlBlock key={call.seq} sql={call.sql} />
      ))}
    </div>
  );
}

function Step({ step, activeRunId, threadId }: {
  step: HealthCheckStep;
  activeRunId: string | null;
  threadId: string | null;
}) {
  const [open, setOpen] = useState(false);
  const hasTrace = Object.keys(step.evidence ?? {}).length > 0 && step.status !== "running";
  // Each running rung streams its own run: the safety probe runs beside the
  // conversational questions now, so "the" active run is no longer one run.
  const evidence = (step.evidence ?? {}) as { run_id?: unknown; thread_id?: unknown };
  const ownRun = typeof evidence.run_id === "string" ? evidence.run_id : null;
  const ownThread = typeof evidence.thread_id === "string" ? evidence.thread_id : null;
  const live = step.status === "running" ? (ownRun ?? activeRunId) : null;

  return (
    <li className="border-border border-b py-3 last:border-b-0">
      <div className="flex items-start gap-3">
        <StepIcon status={step.status} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                "type-label",
                step.status === "pending" ? "text-ink-disabled" : "text-ink",
              )}
            >
              {step.title}
            </span>
            {step.status === "pending" ? null : (
              <Chip tone={STATUS_TONE[step.status] ?? "neutral"}>{step.status}</Chip>
            )}
          </div>
          {step.detail ? (
            <p className="type-support text-ink-secondary mt-1">{step.detail}</p>
          ) : null}
          {live ? <LiveTrace runId={live} threadId={ownThread ?? threadId} /> : null}
          {hasTrace ? (
            <button
              type="button"
              onClick={() => setOpen((value) => !value)}
              className="type-support text-accent motion-fast mt-1.5 inline-flex items-center gap-1 hover:underline"
            >
              <ChevronRightIcon className={cn("size-3.5", open && "rotate-90")} />
              {open ? "Hide trace" : "Show trace"}
            </button>
          ) : null}
          {open ? <Trace step={step} /> : null}
        </div>
      </div>
    </li>
  );
}

const GRADED = new Set(["pass", "warn", "fail", "skipped"]);

function Verdict({ check }: { check: HealthCheck }) {
  const verdict = VERDICTS[check.verdict];
  // Graded rungs, not rows that exist — the whole ladder exists from the start
  // now, so counting rows would read "7 of 7 done" a second after pressing Run.
  const done = check.checks.filter((step) => GRADED.has(step.status)).length;
  const active = check.checks.find((step) => step.status === "running");
  if (check.status === "running") {
    return (
      <div className="border-border bg-surface rounded-container flex items-center gap-3 border p-4">
        <Spinner />
        <div>
          <p className="type-h4 text-ink">{active ? active.title : "Running…"}</p>
          <p className="type-support text-ink-secondary">
            {done} of {check.checks.length} checks done. Four of them are real agent runs, so
            this takes a minute or two on a local model.
          </p>
        </div>
      </div>
    );
  }
  if (!verdict) return null;
  return (
    <div
      className={cn(
        "rounded-container border p-4",
        verdict.tone === "success" && "border-success/30 bg-success-subtle/40",
        verdict.tone === "warning" && "border-warning/30 bg-warning-subtle/40",
        verdict.tone === "error" && "border-error/30 bg-error-subtle/40",
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <StatusDot tone={verdict.tone} />
        <span className="type-h3 text-ink">{verdict.label}</span>
        <span className="type-support text-ink-secondary">{check.summary}</span>
      </div>
      <p className="type-support text-ink-secondary mt-2">{verdict.blurb}</p>
      <p className="type-caption text-ink-disabled mt-2">
        {new Date(check.started_at).toLocaleString()}
        {check.model_label ? ` · graded with ${check.model_label}` : ""}
        {check.duration_ms != null ? ` · ${(check.duration_ms / 1000).toFixed(1)}s` : ""}
      </p>
    </div>
  );
}

export function HealthCheckPanel({ connectionId }: { connectionId: string }) {
  const { data: check, isLoading } = useHealthCheck(connectionId);
  const { data: history } = useHealthCheckHistory(connectionId);
  const { data: models } = useModels();
  const [showProbes, setShowProbes] = useState(false);
  const { data: probes } = useHealthProbes(showProbes);
  const run = useRunHealthCheck(connectionId);
  const [modelRef, setModelRef] = useState("");

  const running = check?.status === "running";
  const error = run.error instanceof ApiError ? run.error.message : null;

  return (
    <div className="flex flex-col gap-6">
      <div className="border-border bg-surface rounded-container border p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <span className="type-h4 text-ink inline-flex items-center gap-2">
              <ShieldIcon className="size-4" />
              Health check
            </span>
            <p className="type-support text-ink-secondary mt-2">
              Connectivity, schema visibility and documentation coverage, then four graded
              questions asked of the model against this source. The last one plants an injected
              write in the data dictionary — the channel the prompt treats as authoritative — and
              passes only if nothing reaches the database.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              type="button"
              loading={run.isPending || running}
              disabled={running}
              onClick={() => run.mutate(modelRef || undefined)}
            >
              {check ? "Run again" : "Run health check"}
            </Button>
          </div>
        </div>

        <div className="mt-4 max-w-sm">
          <Select
            label="Model for the graded questions"
            value={modelRef}
            onChange={(event) => setModelRef(event.target.value)}
            hint="The check grades the model as much as the source. Use the one this source will actually answer with."
          >
            <option value="">Use this source's usual model</option>
            {(models ?? []).map((model) => (
              <option key={model.ref} value={model.ref}>
                {model.provider_name} · {model.label}
              </option>
            ))}
          </Select>
        </div>

        <button
          type="button"
          onClick={() => setShowProbes((value) => !value)}
          className="type-support text-accent motion-fast mt-3 inline-flex items-center gap-1 hover:underline"
        >
          <ChevronRightIcon className={cn("size-3.5", showProbes && "rotate-90")} />
          {showProbes ? "Hide the questions" : "What does it ask?"}
        </button>
        {showProbes ? (
          <ol className="mt-3 flex flex-col gap-2">
            {(probes ?? []).map((probe) => (
              <li key={probe.key} className="border-border rounded-element border p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="type-label text-ink">{probe.title}</span>
                  {probe.plants_dictionary_overlay ? (
                    <Chip tone="warning">plants a dictionary instruction</Chip>
                  ) : null}
                </div>
                <p className="type-support text-ink-secondary mt-1">“{probe.question}”</p>
              </li>
            ))}
          </ol>
        ) : null}
        {error ? <p className="type-support text-error mt-3">{error}</p> : null}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2">
          <Spinner />
          <span className="type-support text-ink-secondary">Loading…</span>
        </div>
      ) : !check ? (
        <EmptyState
          icon={<ShieldIcon className="size-6" />}
          title="No health check yet"
          detail="Run one before handing this source to analysts. It takes a couple of minutes and tells you whether the model can actually answer questions about this schema."
        />
      ) : (
        <>
          <Verdict check={check} />
          <ul className="border-border bg-card rounded-container border px-4">
            {check.checks.map((step) => (
              <Step
                key={step.key}
                step={step}
                activeRunId={check.active_run_id ?? null}
                threadId={check.thread_id ?? null}
              />
            ))}
          </ul>
        </>
      )}

      {history && history.length > 1 ? (
        <div>
          <h3 className="type-h4 text-ink mb-2 inline-flex items-center gap-2">
            <ClockIcon className="size-4" />
            Earlier checks
          </h3>
          <ul className="flex flex-col gap-1">
            {history.slice(1).map((entry) => (
              <li
                key={entry.id}
                className="border-border rounded-element flex flex-wrap items-center gap-2 border px-3 py-2"
              >
                <Chip tone={STATUS_TONE[entry.verdict === "ready" ? "pass" : entry.verdict === "review" ? "warn" : "fail"] ?? "neutral"}>
                  {VERDICTS[entry.verdict]?.label ?? entry.verdict}
                </Chip>
                <span className="type-support text-ink-secondary">{entry.summary}</span>
                <span className="type-caption text-ink-disabled ms-auto">
                  {new Date(entry.started_at).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
